import Collapsible from '@/components/Collapsible'
import FollowButton from '@/components/FollowButton'
import Nip05 from '@/components/Nip05'
import ProfileAbout from '@/components/ProfileAbout'
import { BannerWithLightbox } from '@/components/ProfileBanner'
import ProfileOptions from '@/components/ProfileOptions'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { useFetchFollowings } from '@/hooks/useFetchFollowings'
import { useFetchProfile } from '@/hooks/useFetchProfile'
import { toProfileEditor } from '@/lib/link'
import { useSecondaryPage } from '@/PageManager'
import { HugeiconsIcon } from '@hugeicons/react'
import { Link01Icon, QrCode01Icon } from '@hugeicons/core-free-icons'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import SearchInput from '../SearchInput'
import NotFound from '../NotFound'
import UserAvatar, { AvatarWithLightbox } from '../UserAvatar'
import BlossomServers from './BlossomServers'
import FavoriteRelays from './FavoriteRelays'
import InboxCount from './InboxCount'
import OutboxCount from './OutboxCount'
import FollowedBy from './FollowedBy'
import FollowSets from './FollowSets'
import Followings from './Followings'
import MutedCount from './MutedCount'
import SearchRelays from './SearchRelays'
import BlockedRelays from './BlockedRelays'
import SimpleGroups from './SimpleGroups'
import ProfileFeed from './ProfileFeed'
import Username, { SimpleUsername } from '../Username'
import { normalizeHttpUrl } from '@/lib/url'
import { useCurrentPubkey } from '@/atoms/session'
import { NpubCopy } from '../NpubCopy'
import { isSmallScreenAtom } from '@/atoms/screenSize'
import QrCode from '../QrCode'
import { useAtomValue } from 'jotai'
import { Dialog, DialogContent, DialogHeader, DialogTrigger } from '@/components/ui/dialog'
import { Drawer, DrawerContent, DrawerTrigger } from '@/components/ui/drawer'
import { DialogTitle } from '@radix-ui/react-dialog'
import { loadNostrUser } from '@nostr/gadgets/metadata'
import client from '@/services/client.service'
import { invalidateMetadata } from '@/atoms/invalidation'

export default function Profile({ id }: { id?: string }) {
  const { t } = useTranslation()
  const { push } = useSecondaryPage()
  const { profile, isFetching } = useFetchProfile(id)
  const accountPubkey = useCurrentPubkey()
  const [searchInput, setSearchInput] = useState('')
  const [debouncedInput, setDebouncedInput] = useState(searchInput)
  const { followings } = useFetchFollowings(profile?.pubkey)
  const isFollowingYou = useMemo(() => {
    return (
      !!accountPubkey && accountPubkey !== profile?.pubkey && followings.includes(accountPubkey)
    )
  }, [followings, profile, accountPubkey])
  const [topContainerHeight, setTopContainerHeight] = useState(0)
  const isSelf = accountPubkey === profile?.pubkey
  const [topContainer, setTopContainer] = useState<HTMLDivElement | null>(null)
  const topContainerRef = useCallback((node: HTMLDivElement | null) => {
    if (node) {
      setTopContainer(node)
    }
  }, [])

  // when ?gs= that means we're in the grouped notes list for this profile, so displayTopSection will be false
  const displayTopSection = !new URLSearchParams(window.location.search).get('gs')

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedInput(searchInput.trim())
    }, 1000)

    return () => {
      clearTimeout(handler)
    }
  }, [searchInput])

  useEffect(() => {
    if (isFetching) return
    if (!profile?.pubkey) return
    if (Object.keys(profile?.metadata).length > 0) return
    loadNostrUser({ pubkey: profile.pubkey, refreshStyle: true }).then(async (p) => {
      if (p) client.addUsernameToIndex(p)
      invalidateMetadata()
    })
  }, [isFetching, profile])

  useEffect(() => {
    if (!topContainer) return

    const checkHeight = () => {
      setTopContainerHeight(topContainer.scrollHeight)
    }

    checkHeight()

    const observer = new ResizeObserver(() => {
      checkHeight()
    })

    observer.observe(topContainer)

    return () => {
      observer.disconnect()
    }
  }, [topContainer])

  if (!profile) {
    return (
      <>
        {displayTopSection && (
          <>
            <div>
              <div className="relative bg-cover bg-center mb-2">
                <Skeleton className="w-full aspect-[3/1] rounded-none" />
                <Skeleton className="w-24 h-24 absolute bottom-0 left-3 translate-y-1/2 border-4 border-background rounded-full" />
              </div>
            </div>
            <div className="px-4">
              <Skeleton className="h-5 w-28 mt-14 mb-1" />
              <Skeleton className="h-5 w-56 mt-2 my-1 rounded-full" />
            </div>
          </>
        )}
      </>
    )
  }

  if (!profile) return <NotFound />

  const { pubkey, npub } = profile
  const { banner, about, website } = profile.metadata || {}

  return (
    <>
      {displayTopSection && (
        <div ref={topContainerRef}>
          <div className="relative bg-cover bg-center mb-2">
            <BannerWithLightbox banner={banner} pubkey={pubkey} className="w-full aspect-[3/1]" />
            <AvatarWithLightbox
              userId={pubkey}
              size="large"
              className="absolute left-3 bottom-0 translate-y-1/2 border-4 border-background"
            />
          </div>
          <div className="px-4">
            <div className="flex justify-end h-8 gap-2 items-center">
              <ProfileOptions pubkey={pubkey} />
              {isSelf ? (
                <Button
                  className="w-20 min-w-20 rounded-full"
                  variant="secondary"
                  onClick={() => push(toProfileEditor())}
                >
                  {t('Edit')}
                </Button>
              ) : (
                <FollowButton pubkey={pubkey} />
              )}
            </div>
            {!isSelf && <FollowedBy pubkey={pubkey} />}
            <FollowSets pubkey={pubkey} />
            <div className="pt-2">
              <div className="flex gap-2 items-center">
                <div className="text-xl font-semibold truncate select-text">
                  <SimpleUsername userId={pubkey} />
                </div>
                {isFollowingYou && (
                  <div className="text-muted-foreground rounded-full bg-muted text-xs h-fit px-2 shrink-0">
                    {t('Follows you')}
                  </div>
                )}
              </div>
              <Nip05 pubkey={pubkey} />
              <div className="flex gap-1 mt-1">
                <NpubCopy npub={npub} />
                <NpubQrCode pubkey={pubkey} npub={npub} />
              </div>
              <Collapsible>
                <ProfileAbout
                  about={about}
                  className="text-wrap break-words whitespace-pre-wrap mt-2 select-text"
                />
              </Collapsible>
              {website && (
                <div className="flex gap-1 items-center text-primary mt-2 truncate select-text">
                  <HugeiconsIcon icon={Link01Icon} size={14} className="shrink-0" />
                  <a
                    href={normalizeHttpUrl(website)}
                    target="_blank"
                    className="hover:underline truncate flex-1 max-w-fit w-0"
                  >
                    {website}
                  </a>
                </div>
              )}
              <div className="grid grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-x-4 gap-y-1 mt-2 text-xs">
                <Followings pubkey={pubkey} />
                <InboxCount pubkey={pubkey} />
                <OutboxCount pubkey={pubkey} />
                <FavoriteRelays pubkey={pubkey} />
                <BlossomServers pubkey={pubkey} />
                <MutedCount pubkey={pubkey} />
                <SearchRelays pubkey={pubkey} />
                <BlockedRelays pubkey={pubkey} />
                <SimpleGroups pubkey={pubkey} />
              </div>
            </div>
          </div>
          <div className="px-4 pt-2 pb-0.5">
            <SearchInput
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder={t('Search')}
            />
          </div>
        </div>
      )}
      <ProfileFeed
        pubkey={pubkey}
        search={debouncedInput}
        topSpace={displayTopSection ? topContainerHeight + 100 : 0}
      />
    </>
  )
}

function NpubQrCode({ pubkey, npub }: { pubkey: string; npub: string }) {
  const isSmallScreen = useAtomValue(isSmallScreenAtom)

  const trigger = (
    <div className="bg-muted rounded-full h-5 w-5 flex flex-col items-center justify-center text-muted-foreground hover:text-foreground">
      <HugeiconsIcon icon={QrCode01Icon} size={14} />
    </div>
  )

  const content = (
    <div className="w-full flex flex-col items-center gap-4 p-8">
      <div className="flex items-center w-full gap-2 pointer-events-none px-1">
        <UserAvatar size="big" userId={pubkey} />
        <div className="flex-1 w-0">
          <Username userId={pubkey} className="text-2xl font-semibold truncate" />
          <Nip05 pubkey={pubkey} />
        </div>
      </div>
      <QrCode size={512} value={`nostr:${npub}`} />
      <div className="flex flex-col items-center">
        <NpubCopy npub={npub} />
      </div>
    </div>
  )

  if (isSmallScreen) {
    return (
      <Drawer>
        <DrawerTrigger>{trigger}</DrawerTrigger>
        <DrawerContent>{content}</DrawerContent>
      </Drawer>
    )
  }

  return (
    <Dialog>
      <DialogHeader className="hidden">
        <DialogTitle>Npub QR Code</DialogTitle>
      </DialogHeader>
      <DialogTrigger>{trigger}</DialogTrigger>
      <DialogContent className="w-80 p-0 m-0" onOpenAutoFocus={(e) => e.preventDefault()}>
        {content}
      </DialogContent>
    </Dialog>
  )
}
